/**
 * Système de style partagé des graphiques ECharts du builder Orion.
 *
 * Porté de Metrix (`frontend/src/lib/chartStyle.ts`), re-skinné aux tokens Orion
 * (palette hex du builder, pas de CSS vars oklch côté Orion).
 *
 * L'apport central par rapport aux options ad-hoc précédentes : `buildChartGrid`
 * **réserve** l'espace de la légende et des axes, ce qui supprime le chevauchement
 * légende/axe X ; et `buildXAxisLabel` amincit les libellés quand les points sont
 * trop nombreux (30+ dates ne se marchent plus dessus).
 */
import { formatDateLabel } from './chartFormat';
import { formatMetricValue, type MetricFormat } from './formatValue';

export type LegendPosition = 'top' | 'bottom' | 'left' | 'right';

/** Palette de séries du builder (tokens Orion). */
export const CHART_COLORS = [
  '#6366F1',
  '#22C55E',
  '#F59E0B',
  '#EC4899',
  '#0EA5E9',
  '#14B8A6',
  '#A855F7',
  '#F97316',
];

export const COLOR_PRIMARY = '#6366F1';
/** Série de comparaison (période précédente) — gris neutre. */
export const COLOR_PREVIOUS = '#C8CBD8';

const TEXT_STRONG = '#1B2559';
const AXIS_LINE_COLOR = '#E2E4ED';
const AXIS_LABEL_COLOR = '#8B8FA7';
const GRID_LINE_COLOR = '#EDEEF4';
const TOOLTIP_BG = '#1B2559';
const TOOLTIP_FG = '#FFFFFF';

export const AXIS_X_STYLE = {
  axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
  axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 10 },
  axisTick: { show: false },
} as const;

export const AXIS_Y_STYLE = {
  axisLine: { show: false },
  splitLine: { lineStyle: { type: 'dashed' as const, color: GRID_LINE_COLOR } },
  axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 10 },
} as const;

export const TOOLTIP_STYLE = {
  backgroundColor: TOOLTIP_BG,
  textStyle: { color: TOOLTIP_FG, fontSize: 12 },
  borderColor: TOOLTIP_BG,
  borderRadius: 8,
  // Sans ça, ECharts monte le tooltip DANS le conteneur du widget : un widget voisin plus
  // bas dans le DOM (donc empilé au-dessus par défaut) le recouvre dès qu'il déborde vers
  // lui. `appendToBody` le sort sur <body>, au-dessus de tous les widgets.
  appendToBody: true,
} as const;

export const TITLE_STYLE = {
  textStyle: { fontSize: 14, fontWeight: 600, color: TEXT_STRONG },
} as const;

/** Dégradé vertical sous une courbe (série unique). */
export function buildAreaGradient(color: string) {
  return {
    color: {
      type: 'linear' as const,
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: `${color}30` },
        { offset: 1, color: `${color}05` },
      ],
    },
  };
}

/**
 * Libellés de l'axe X : formatage des buckets temps, rotation et amincissement
 * quand les points sont nombreux. Si la série couvre plusieurs années, l'année
 * est ajoutée pour lever l'ambiguïté.
 */
export function buildXAxisLabel(dataLength: number, values?: string[]) {
  const spansMultipleYears =
    !!values && values.length >= 2 && values[0]?.slice(0, 4) !== values[values.length - 1]?.slice(0, 4);

  const formatter = spansMultipleYears
    ? (value: string) => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(2, 4)}`;
        if (/^\d{8}$/.test(value)) return `${value.slice(6, 8)}/${value.slice(4, 6)}/${value.slice(2, 4)}`;
        return formatDateLabel(value);
      }
    : formatDateLabel;

  return {
    ...AXIS_X_STYLE.axisLabel,
    formatter,
    rotate: dataLength > 60 ? 45 : dataLength > 12 ? 30 : 0,
    // Au-delà de ~20 points, on n'affiche qu'un libellé sur N pour éviter le tassement.
    interval: dataLength > 20 ? Math.floor(dataLength / 12) : undefined,
  };
}

const LEGEND_LINE_MAX = 20;

/** Tronque un nom de série trop long, sur 2 lignes si une coupure de mot est possible. */
function truncateLegend(name: string, maxPerLine: number): string {
  if (name.length <= maxPerLine) return name;
  const breakAt = name.lastIndexOf(' ', maxPerLine);
  const splitIdx = breakAt > maxPerLine * 0.3 ? breakAt : name.lastIndexOf('-', maxPerLine);
  const cut = splitIdx > maxPerLine * 0.3 ? splitIdx + 1 : maxPerLine;
  const line1 = name.slice(0, cut).trimEnd();
  const line2 = name.slice(cut).trimStart();
  if (line2.length > maxPerLine) return `${line1}\n${line2.slice(0, maxPerLine - 1)}…`;
  return `${line1}\n${line2}`;
}

export function buildLegendStyle(
  data?: string[],
  position?: LegendPosition,
  hasTitle?: boolean,
  offset?: number,
) {
  const pos = position || 'bottom';
  const off = offset ?? 0;
  const isVertical = pos === 'left' || pos === 'right';

  const placement: Record<string, unknown> = {};
  if (pos === 'bottom') placement.bottom = 0;
  else if (pos === 'top') placement.top = hasTitle ? 38 : 8;
  else if (pos === 'left') {
    placement.left = `${1 + off}%`;
    placement.orient = 'vertical';
    placement.top = 'middle';
  } else if (pos === 'right') {
    placement.right = `${1 + off}%`;
    placement.orient = 'vertical';
    placement.top = 'middle';
  }

  return {
    ...(data ? { data } : {}),
    ...placement,
    type: 'plain' as const,
    textStyle: {
      color: AXIS_LABEL_COLOR,
      fontSize: 11,
      width: isVertical ? 120 : undefined,
      overflow: isVertical ? ('truncate' as const) : undefined,
    },
    formatter: (name: string) => truncateLegend(name, LEGEND_LINE_MAX),
    tooltip: { show: true },
    itemWidth: 10,
    itemHeight: 10,
    itemGap: isVertical ? 8 : 12,
  };
}

export interface ChartGridOptions {
  hasTitle?: boolean;
  showYAxis?: boolean;
  dualYAxis?: boolean;
  showLegend?: boolean;
  legendPosition?: LegendPosition;
  /** Libellés d'axe X pivotés → besoin de plus de marge en bas. */
  rotatedXLabels?: boolean;
}

/**
 * Grille du graphique : réserve l'espace de la légende (selon sa position) et des
 * axes. C'est ce calcul qui évite que la légende chevauche l'axe X.
 */
export function buildChartGrid(opts: ChartGridOptions) {
  const { hasTitle, showYAxis = true, dualYAxis, showLegend, legendPosition, rotatedXLabels } = opts;

  const pos = legendPosition || 'bottom';
  const legendLeft = showLegend && pos === 'left';
  const legendRight = showLegend && pos === 'right';
  const legendTop = showLegend && pos === 'top';
  const legendBottom = showLegend && pos === 'bottom';

  const bottomBase = rotatedXLabels ? 44 : 28;

  return {
    left: legendLeft ? '35%' : showYAxis ? (dualYAxis ? 60 : 52) : 12,
    right: legendRight ? '35%' : dualYAxis ? 60 : 20,
    top: legendTop ? (hasTitle ? 76 : 50) : hasTitle ? 40 : 16,
    // Légende en bas → on empile la hauteur de la légende SOUS les libellés d'axe.
    bottom: legendBottom ? bottomBase + 24 : bottomBase,
    containLabel: false,
    // Pas de `show` ici : dans ECharts, `grid.show` dessine un cadre autour de la zone de
    // tracé, pas les lignes de grille — celles-ci sont pilotées par `yAxis.splitLine`.
  };
}

/** Une ligne du tooltip ECharts (`trigger: 'axis'`). */
interface TooltipParam {
  axisValueLabel?: string;
  axisValue?: string;
  marker?: string;
  seriesName?: string;
  value?: number;
}

/**
 * ECharts insère la chaîne renvoyée par `formatter` en innerHTML. Les libellés d'axe et
 * les noms d'item sont des valeurs de dimension brutes (titre de page, nom de campagne,
 * source…) : données externes, donc échappées avant concaténation. Unique définition —
 * les tooltips d'item (`widgets/categoryItems.ts`) l'importent d'ici.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Tooltip d'axe avec valeurs formatées selon le format de chaque métrique
 * (nombre / % / € / durée) — `formats` est indexé par nom de série.
 */
export function buildTooltip(formats: Record<string, MetricFormat>) {
  return {
    ...TOOLTIP_STYLE,
    trigger: 'axis' as const,
    axisPointer: { type: 'line' as const, lineStyle: { color: AXIS_LINE_COLOR } },
    formatter: (params: TooltipParam[] | TooltipParam) => {
      const list = Array.isArray(params) ? params : [params];
      if (!list.length) return '';
      const header = escapeHtml(String(list[0].axisValueLabel ?? list[0].axisValue ?? ''));
      const lines = list.map((p) => {
        const name = p.seriesName ?? '';
        const value = formatMetricValue(p.value ?? 0, formats[name] ?? 'number');
        // `marker` est un fragment HTML généré par ECharts (pastille de couleur), pas une donnée.
        return `${p.marker ?? ''} ${escapeHtml(name)} <b>${escapeHtml(value)}</b>`;
      });
      return [header, ...lines].join('<br/>');
    },
  };
}

/**
 * Deux axes Y : gauche (défaut) + droit. Utilisé quand au moins une série est assignée
 * à l'axe droit (`display.series_y_axis[metric] === 1`). L'axe droit ne dessine pas de
 * grille (le `splitLine` du gauche suffit ; deux grilles produisent un moiré illisible).
 */
export function buildDualYAxis(
  leftFormat: MetricFormat,
  rightFormat: MetricFormat,
  showGrid: boolean,
  showYAxis: boolean,
  scale: 'linear' | 'log' = 'linear',
) {
  const axisType = scale === 'log' ? ('log' as const) : ('value' as const);
  return [
    {
      type: axisType,
      show: showYAxis,
      ...AXIS_Y_STYLE,
      splitLine: showGrid ? AXIS_Y_STYLE.splitLine : { show: false },
      axisLabel: buildYAxisLabel(leftFormat),
    },
    {
      type: axisType,
      show: showYAxis,
      position: 'right' as const,
      ...AXIS_Y_STYLE,
      splitLine: { show: false },
      axisLabel: buildYAxisLabel(rightFormat),
    },
  ];
}

/**
 * Résout la configuration d'axe Y d'un chart cartésien (line/bar/area) selon
 * l'assignation par série (`display.series_y_axis`). Factorise la logique dupliquée
 * dans les 3 widgets.
 *
 * Double axe UNIQUEMENT si des séries existent des DEUX côtés : si tout est à gauche
 * (défaut) ou tout à droite, on retombe sur un axe unique (sinon l'axe gauche serait
 * dessiné sans aucune série rattachée). `axisIndexFor` renvoie 0 hors mode double.
 */
export function resolveDualAxis(
  metrics: string[],
  seriesYAxis: Record<string, 0 | 1> | undefined,
  formatOf: (metric: string) => MetricFormat,
  showGrid: boolean,
  showYAxis: boolean,
  scale: 'linear' | 'log' = 'linear',
) {
  const assign = seriesYAxis ?? {};
  const rawAxis = (m: string) => (assign[m] === 1 ? 1 : 0);
  const dual = metrics.some((m) => rawAxis(m) === 0) && metrics.some((m) => rawAxis(m) === 1);
  const axisIndexFor = (m: string) => (dual ? rawAxis(m) : 0);
  const axisType = scale === 'log' ? ('log' as const) : ('value' as const);

  const singleAxis = {
    type: axisType,
    show: showYAxis,
    ...AXIS_Y_STYLE,
    splitLine: showGrid ? AXIS_Y_STYLE.splitLine : { show: false },
    axisLabel: buildYAxisLabel(formatOf(metrics[0] ?? '')),
  };

  if (!dual) return { yAxis: singleAxis, axisIndexFor, dual };

  const leftMetric = metrics.find((m) => rawAxis(m) === 0) ?? metrics[0] ?? '';
  const rightMetric = metrics.find((m) => rawAxis(m) === 1) ?? leftMetric;
  return {
    yAxis: buildDualYAxis(formatOf(leftMetric), formatOf(rightMetric), showGrid, showYAxis, scale),
    axisIndexFor,
    dual,
  };
}

/**
 * Remplace les valeurs ≤0 par `null` quand l'axe est en échelle log — log(0) est indéfini
 * et ECharts ne les traite PAS comme des trous tant qu'elles restent des nombres (0 reste
 * un point invalide mais "présent", la courbe se casse sans que `connectNulls` n'ait prise
 * dessus). Un vrai `null` est le seul signal qu'ECharts sait ponter avec `connectNulls`.
 * En échelle linéaire, les valeurs passent inchangées.
 */
export function maskNonPositiveForLog(
  values: number[],
  scale: 'linear' | 'log',
): (number | null)[] {
  if (scale !== 'log') return values;
  return values.map((v) => (v > 0 ? v : null));
}

/** Libellés de l'axe Y formatés selon le format de la métrique principale. */
export function buildYAxisLabel(format: MetricFormat) {
  return {
    ...AXIS_Y_STYLE.axisLabel,
    formatter: (value: number) =>
      format === 'number' && Math.abs(value) >= 1000
        ? `${(value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}k`
        : formatMetricValue(value, format, format === 'currency' ? { decimals: 0 } : undefined),
  };
}
